// Shared cleanroom takeoff math — single source of truth for room-parametric
// calculations. Used by the standalone DuctEstimator page and the per-revision
// Room Takeoff module (src/pages/estimate/RoomTakeoff.jsx). Everything is a
// function of room dimensions: CFM from volume x ACH, HEPA counts from CFM,
// duct diameters from CFM at design velocities.

// Duct design velocities — admin-tunable via Setup > Basis of Design >
// Mechanical/HVAC (db/65_takeoff_variables.sql, Category='hvac_duct_sizing')
// as of 2026-09-10; DEFAULT_DUCT_TUNABLES is the fallback every function
// below defaults to when no live-fetched override is passed in, so every
// existing caller (including RoomTakeoff.jsx's own calcRoom() calls) that
// omits the argument behaves identically to before this existed.
export const DEFAULT_DUCT_TUNABLES = {
  TRUNK_FPM: 1000,
  BRANCH_FPM: 700,
  RETURN_FPM: 800,
  EXHAUST_FPM: 900,
  // Fallback design airflow per terminal HEPA — used ONLY when a room's
  // selected Terminal filtration option carries no DesignAirflowCfm of its
  // own (roomSystemsEngine.js resolves the option first). Seeded as a
  // TakeoffVariables row (db/68, Category='hepa_sizing'); was a bare `500`
  // literal here until 2026-09-11.
  HEPA_DEFAULT_DESIGN_CFM: 500,
};
export const TRUNK_FPM   = DEFAULT_DUCT_TUNABLES.TRUNK_FPM;
export const BRANCH_FPM  = DEFAULT_DUCT_TUNABLES.BRANCH_FPM;
export const RETURN_FPM  = DEFAULT_DUCT_TUNABLES.RETURN_FPM;
export const EXHAUST_FPM = DEFAULT_DUCT_TUNABLES.EXHAUST_FPM;

export const MATERIAL_FACTOR = { galvanized: 1.0, stainless: 1.05, aluminum: 0.35 };
export const MATERIAL_LABELS = { galvanized: "Galvanized Steel", stainless: "Stainless Steel", aluminum: "Aluminum" };
export const ROUND_SIZES = [4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,22,24,26,28,30,32,36,40,44,48];

export const GALV_LBS = {
  4:0.75,5:0.9,6:1.1,7:1.3,8:1.5,9:1.7,10:1.9,11:2.1,12:2.3,
  13:2.5,14:2.7,15:2.9,16:3.1,17:3.3,18:3.5,19:3.7,20:3.9,
  22:4.3,24:4.7,26:5.1,28:5.5,30:5.9,32:6.3,36:7.1,40:7.9,44:8.7,48:9.5,
};

export const ROOM_TYPES = [
  { value:"iso5",       label:"ISO 5 Cleanroom",  achMin:240, achMax:480, achDefault:360 },
  { value:"iso6",       label:"ISO 6 Cleanroom",  achMin:90,  achMax:180, achDefault:120 },
  { value:"iso7",       label:"ISO 7 Cleanroom",  achMin:30,  achMax:60,  achDefault:45  },
  { value:"iso8",       label:"ISO 8 Cleanroom",  achMin:10,  achMax:25,  achDefault:15  },
  { value:"gowning",    label:"Gowning Room",      achMin:15,  achMax:40,  achDefault:25  },
  { value:"airlock",    label:"Airlock / Anteroom",achMin:20,  achMax:60,  achDefault:30  },
  { value:"lab",        label:"Laboratory",        achMin:10,  achMax:12,  achDefault:10  },
  { value:"office",     label:"Office",            achMin:6,   achMax:10,  achDefault:8   },
  { value:"corridor",   label:"Corridor",          achMin:4,   achMax:8,   achDefault:6   },
  { value:"mechanical", label:"Mechanical",        achMin:4,   achMax:6,   achDefault:4   },
  { value:"oa100",      label:"100% OA Room",      achMin:12,  achMax:20,  achDefault:15  },
  { value:"custom",     label:"Custom",            achMin:1,   achMax:600, achDefault:10  },
];

export const AHU_TYPES = [
  { value:"recirculating", label:"Recirculating (30% OA)" },
  { value:"oa100",         label:"100% Outside Air"       },
];

export function roundDia(cfm, fpm) {
  if (!cfm || cfm <= 0) return 6;
  const areaSqIn = (cfm / fpm) * 144;
  const raw = Math.sqrt((4 * areaSqIn) / Math.PI);
  return ROUND_SIZES.find(s => s >= raw) || 48;
}

export function lbsPerFt(dia, material) {
  const base = GALV_LBS[dia] ?? (dia * 0.197);
  return base * (MATERIAL_FACTOR[material] ?? 1.0);
}

// Core per-room derivation. room: { length, width, height, ach }. A caller
// that already knows a room's TRUE (possibly non-rectangular) floor area —
// see takeoffGeometry.js's polygonArea(), fed by AI-traced PolygonJson — can
// pass areaOverrideFt2 so volume/CFM/HEPA math uses the real footprint
// instead of overstating it via the bounding-box L*W every polygon room
// still carries for backward compatibility with rect-only canvas features.
//
// HEPA count: the engineering layer (roomSystemsEngine.js — airflow AND
// ceiling-coverage constraints, plus an estimator override) owns the real
// answer. A caller that has run it passes the result through as
// room.hepaCountOverride (the EFFECTIVE count) and room.designCfmPerHepa (the
// selected filter's design airflow) so the duct sizing below follows it; a
// caller without criteria (the standalone Duct Estimator) gets the plain
// airflow-only count at the tunable default design airflow. The old bare
// `/ 500` literal is gone either way.
export function calcRoom(room, tunables = DEFAULT_DUCT_TUNABLES) {
  const floorSF = room.areaOverrideFt2 ?? (room.length * room.width);
  const vol = floorSF * room.height;
  const cfm = (vol * room.ach) / 60;
  const designCfmPerHepa = +room.designCfmPerHepa > 0
    ? +room.designCfmPerHepa
    : (tunables.HEPA_DEFAULT_DESIGN_CFM ?? DEFAULT_DUCT_TUNABLES.HEPA_DEFAULT_DESIGN_CFM);
  const hepaCount = room.hepaCountOverride != null && +room.hepaCountOverride >= 0
    ? Math.round(+room.hepaCountOverride)
    : Math.ceil(cfm / designCfmPerHepa);
  const cfmPerHepa = hepaCount > 0 ? cfm / hepaCount : 0;
  return {
    vol, cfm, hepaCount, designCfmPerHepa,
    cfmPerHepa,
    branchDia:  roundDia(cfmPerHepa, tunables.BRANCH_FPM),
    returnDia:  roundDia(cfm, tunables.RETURN_FPM),
    exhaustDia: roundDia(cfm, tunables.EXHAUST_FPM),
  };
}

// Architectural areas from the same dimensions. Gross wall SF counts every
// room's full perimeter — shared-wall dedup requires the layout canvas
// (adjacency), so callers should label this figure "gross". areaOverrideFt2/
// perimeterOverrideFt: see calcRoom's comment — a polygon room's true area/
// perimeter, when known, in place of the L*W/2*(L+W) bounding-box math.
export function calcAreas(room) {
  const floorSF = room.areaOverrideFt2 ?? (room.length * room.width);
  const perimeterLF = room.perimeterOverrideFt ?? (2 * (Number(room.length) + Number(room.width)));
  return {
    floorSF,
    ceilingSF: floorSF,
    grossWallSF: perimeterLF * room.height,
    perimeterLF,
  };
}
